import type { IPublicClientApplication, AccountInfo, SilentRequest } from '@azure/msal-browser';
import { loginRequest } from '~/services/auth-service';

/**
 * Custom error for authentication failures
 */
export class AuthenticationError extends Error {
  constructor(message: string, public readonly cause?: unknown) {
    super(message);
    this.name = 'AuthenticationError';
  }
}

/**
 * Custom error for token retrieval failures
 */
export class TokenRetrievalError extends AuthenticationError {
  constructor(message: string, cause?: unknown) {
    super(message, cause);
    this.name = 'TokenRetrievalError';
  }
}

/**
 * Get the primary account from MSAL instance
 */
function getPrimaryAccount(instance: IPublicClientApplication): AccountInfo | null {
  const activeAccount = instance.getActiveAccount();
  if (activeAccount) return activeAccount;
  
  const accounts = instance.getAllAccounts();
  return accounts[0] ?? null;
}

/**
 * Get the ID token from MSAL using proper SDK methods
 * This function retrieves a fresh or cached ID token from MSAL
 * @param msalInstance - The MSAL instance
 * @throws {TokenRetrievalError} When token cannot be retrieved
 */
export async function getIdToken(msalInstance: IPublicClientApplication): Promise<string> {
  try {
    const account = getPrimaryAccount(msalInstance);
    
    if (!account) {
      throw new TokenRetrievalError('No active account found. User needs to sign in.');
    }

    // Try to acquire token silently first (this will use cached token if valid)
    try {
      const request: SilentRequest = {
        ...loginRequest,
        account,
      };
      
      const response = await msalInstance.acquireTokenSilent(request);
      
      if (!response.idToken) {
        throw new TokenRetrievalError('ID token not found in response');
      }
      
      return response.idToken;
    } catch (silentError) {
      // If silent acquisition fails, fallback to cached token from account
      if (account.idToken) {
        return account.idToken;
      }
      
      throw new TokenRetrievalError(
        'Failed to acquire token silently and no cached token available',
        silentError
      );
    }
  } catch (error) {
    if (error instanceof TokenRetrievalError) {
      throw error;
    }
    throw new TokenRetrievalError('Failed to retrieve ID token', error);
  }
}

/**
 * Get the ID token synchronously from the active account
 * This should only be used in contexts where async is not possible (e.g., axios interceptors)
 * WARNING: This returns cached token which might be expired. Prefer getIdToken() when possible.
 * @param msalInstance - The MSAL instance
 * @returns ID token or null if not available
 */
export function getIdTokenSync(msalInstance: IPublicClientApplication): string | null {
  try {
    const account = getPrimaryAccount(msalInstance);
    return account?.idToken ?? null;
  } catch (error) {
    console.error('Error retrieving ID token synchronously:', error);
    return null;
  }
}
